/**
 * @fileoverview coordFormat — lat/lon formatting for the Dry Run Summary row explorer.
 * @module import-editor/utils
 *
 * Responsibilities:
 * - Produce compact 2-decimal in-cell strings so the row table stays readable
 *   at narrow widths (the Apr 24 UX decision — short in cell, precise in
 *   tooltip).
 * - Produce full 6-decimal precise strings for the hover tooltip / CSV export
 *   so power users can copy usable coordinates.
 *
 * Dependencies:
 * - None (pure).
 *
 * Architecture:
 * - Pure. No DOM, no side effects. Safe to run in SSR and in workers.
 *
 * Notes:
 * - Input coordinates are ALWAYS [longitude, latitude] tuples — GeoJSON
 *   convention. Display order is "lat, lon" because users think in those
 *   terms. If you ever see "lon, lat" in UI output, this util was bypassed.
 */

export type LonLat = [number, number] | null | undefined;

const COMPACT_DECIMALS = 2;
const PRECISE_DECIMALS = 6;

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * Compact "lat, lon" string with 2 decimals per axis. Used inside the row
 * grid where column width is scarce. Returns an em-dash when input is
 * missing so every row renders something stable.
 */
export function formatCoordShort(coords: LonLat): string {
  if (!coords) return '—';
  const [lon, lat] = coords;
  if (!isFiniteNumber(lon) || !isFiniteNumber(lat)) return '—';
  return `${lat.toFixed(COMPACT_DECIMALS)}, ${lon.toFixed(COMPACT_DECIMALS)}`;
}

/**
 * Precise "lat, lon" string with 6 decimals per axis (~11 cm at the
 * equator — more than enough for parcel-level diagnostics). Used in
 * hover tooltips and in the CSV export.
 */
export function formatCoordPrecise(coords: LonLat): string {
  if (!coords) return '';
  const [lon, lat] = coords;
  if (!isFiniteNumber(lon) || !isFiniteNumber(lat)) return '';
  return `${lat.toFixed(PRECISE_DECIMALS)}, ${lon.toFixed(PRECISE_DECIMALS)}`;
}
